import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './context/AuthContext';
import { apiGet, apiGetOwnershipXml, apiPost } from './api/client';
import LvSummaryAll from './components/LvSummaryAll';
import { usePdfLvSummary } from './hooks/usePdfLvSummary';
import { buildLvNumberPayload } from './utils/buildLvNumberPayload';
import { formatSavedPropertyLabel, isPdfBackedProperty } from './utils/propertyLabel';
import { fetchStoredLvPdf } from './utils/fetchStoredLvPdf';
import { useStoredLvPdfHydration } from './hooks/useStoredLvPdfHydration';
import { detectPropertyTypeFromSummary, PROPERTY_TYPE_VALUES } from './utils/propertyType';
import { DOCUMENT_KEYS, loadDocumentDraft, saveDocumentDraft } from './utils/documentDrafts';

const LAST_KU_LV_KEY = 'realty-cuzk-last-ku-lv';

function toOptionalString(v) {
  const s = (v ?? '').toString().trim();
  return s === '' ? '' : s;
}

export default function SaleFlatOrderPage({
  onBack,
  onSetupRequired,
  initialProperty,
  onSwitchToRentOrder,
  isAdmin = false,
}) {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const selectedSourceUserId = initialProperty?.user_id ? String(initialProperty.user_id) : '';

  // KU + LV
  const [ku, setKu] = useState('');
  const [lv, setLv] = useState('');
  const [loadLoading, setLoadLoading] = useState(false);
  const [error, setError] = useState(null);

  // Loaded LV XML
  const [xml, setXml] = useState(null);
  const [summary, setSummary] = useState(null);

  // User inputs for template placeholders
  const [floor, setFloor] = useState('');
  const [ico, setIco] = useState('');
  const [dispozice, setDispozice] = useState('');
  const [vymeraM2, setVymeraM2] = useState('');
  const [ulice, setUlice] = useState('');
  const [co, setCo] = useState('');
  const [obec, setObec] = useState('');
  const [psc, setPsc] = useState('');

  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [gdprLoading, setGdprLoading] = useState(false);
  const [gdprError, setGdprError] = useState(null);
  const [pdfBase64, setPdfBase64] = useState('');
  const [, setPdfName] = useState('');
  const [savedProperties, setSavedProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [overrideDetectedType, setOverrideDetectedType] = useState(false);
  const [overrideType, setOverrideType] = useState(PROPERTY_TYPE_VALUES.LAND);

  const { pdfSummary } = usePdfLvSummary(pdfBase64, getToken);

  const autoLoadKeyRef = useRef('');

  useStoredLvPdfHydration(initialProperty, getToken, {
    setPdfBase64,
    setPdfName,
    setXml,
    setSummary,
    setError,
    setLoadLoading,
    translateError: () => t('saleFlatOrder.errorFetch'),
    allowTargetUserId: isAdmin,
  });

  const kuTrim = (ku || '').trim();
  const lvTrim = (lv || '').trim();
  const canLoadLv = kuTrim.length > 0 && lvTrim.length > 0 && !loadLoading;

  const floorNum = floor === '' ? null : (parseInt(floor, 10) | 0);
  const detectedPropertyType = detectPropertyTypeFromSummary(summary || pdfSummary);

  const hasXml = typeof xml === 'string' && xml.trim().length > 0;
  const hasSource = hasXml || !!pdfBase64;
  const canGenerate = hasXml && !generateLoading;

  const persistLastKuLv = useCallback((k, l) => {
    try {
      sessionStorage.setItem(LAST_KU_LV_KEY, JSON.stringify({ ku: k, lv: l }));
    } catch {
      /* ignore */
    }
  }, []);

  const hydrateDraftForKuLv = useCallback(async (k, l) => {
    try {
      const draft = await loadDocumentDraft(
        {
          kod_ku: k,
          cislo_lv: l,
          document_key: DOCUMENT_KEYS.SALE_ORDER_FLAT,
          user_id: selectedSourceUserId || undefined,
        },
        getToken,
      );
      const payload = draft?.payload;
      if (!payload || typeof payload !== 'object') return;
      setFloor(payload.floor == null ? '' : String(payload.floor));
      setIco(payload.ico == null ? '' : String(payload.ico));
      setDispozice(payload.dispozice == null ? '' : String(payload.dispozice));
      setVymeraM2(payload.vymeraM2 == null ? '' : String(payload.vymeraM2));
      setUlice(payload.ulice == null ? '' : String(payload.ulice));
      setCo(payload.co == null ? '' : String(payload.co));
      setObec(payload.obec == null ? '' : String(payload.obec));
      setPsc(payload.psc == null ? '' : String(payload.psc));
      if (payload.propertyType) {
        setOverrideDetectedType(true);
        setOverrideType(String(payload.propertyType));
      }
    } catch (draftErr) {
      console.warn('saleFlatOrder: failed to load saved draft', draftErr.message);
    }
  }, [getToken, selectedSourceUserId]);

  const loadXmlForKuLv = useCallback(
    async (kRaw, lRaw) => {
      const k = String(kRaw ?? '').trim();
      const l = String(lRaw ?? '').trim();
      if (!k || !l) return;

      setError(null);
      setLoadLoading(true);
      try {
        const { res, data } = await apiGetOwnershipXml({
          KodKU: k,
          CisloLv: l,
          userId: selectedSourceUserId || undefined,
          getToken,
          confirmMessage: t('lvWorkspace.refreshWarning'),
          allowPaidFetch: true,
        });

        if (res.status === 403 && data.code === 'SETUP_REQUIRED' && onSetupRequired) {
          onSetupRequired();
          return;
        }

        if (!res.ok) {
          throw new Error(data.error || t('saleFlatOrder.errorFetch'));
        }
        if (!data.xml || typeof data.xml !== 'string') {
          throw new Error(t('saleFlatOrder.errorNoXml'));
        }

        setXml(data.xml);
        setSummary(data.summary || null);
        setGenerateError(null);
        persistLastKuLv(k, l);
        await hydrateDraftForKuLv(k, l);
      } catch (err) {
        setError(err.message || t('saleFlatOrder.errorUnknown'));
      } finally {
        setLoadLoading(false);
      }
    },
    [getToken, hydrateDraftForKuLv, onSetupRequired, persistLastKuLv, selectedSourceUserId, t],
  );

  // Restore last KU/LV from session when opening without a pre-selected property
  useEffect(() => {
    if (initialProperty) return;
    try {
      const raw = sessionStorage.getItem(LAST_KU_LV_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const k = parsed?.ku != null ? String(parsed.ku).trim() : '';
      const l = parsed?.lv != null ? String(parsed.lv).trim() : '';
      if (k && l) {
        setKu(k);
        setLv(l);
      }
    } catch {
      /* ignore */
    }
  }, [initialProperty]);

  useEffect(() => {
    if (!initialProperty?.kod_ku || !initialProperty?.cislo_lv) {
      autoLoadKeyRef.current = '';
    }
  }, [initialProperty]);

  // Prefill + auto-load LV when opened from My properties (or cross-nav from rent)
  useEffect(() => {
    if (!initialProperty?.kod_ku || !initialProperty?.cislo_lv) {
      autoLoadKeyRef.current = '';
      return;
    }
    const k = String(initialProperty.kod_ku).trim();
    const l = String(initialProperty.cislo_lv).trim();
    if (!k || !l) return;

    const key = `${k}-${l}`;
    if (autoLoadKeyRef.current === key) return;
    autoLoadKeyRef.current = key;

    setKu(k);
    setLv(l);
    if (initialProperty.pdfBase64) return;
    const path = initialProperty.lv_pdf_storage_path || initialProperty.lvPdfStoragePath;
    if (path) return;
    loadXmlForKuLv(k, l);
  }, [initialProperty, loadXmlForKuLv]);

  useEffect(() => {
    if (!initialProperty?.pdfBase64) return;
    setPdfBase64(String(initialProperty.pdfBase64));
    setPdfName(String(initialProperty.pdfName || 'LV.pdf'));
    setXml(null);
    setSummary(null);
    setError(null);
  }, [initialProperty]);

  useEffect(() => {
    if (!initialProperty) return;
    const hasOverride = Boolean(initialProperty.property_type_override_enabled);
    const incomingType = String(initialProperty.property_type_override || '').trim();
    if (hasOverride && incomingType) {
      setOverrideDetectedType(true);
      setOverrideType(incomingType);
      return;
    }
    setOverrideDetectedType(false);
  }, [initialProperty]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet('/api/properties', getToken);
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && Array.isArray(data.properties)) {
          setSavedProperties(data.properties);
        }
      } catch {
        if (!cancelled) setSavedProperties([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    // Reset generate errors when user changes KU/LV inputs.
    setGenerateError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ku, lv]);

  const handleLoadLv = async (e) => {
    e.preventDefault();
    if (hasSource) return;
    if (!isAdmin || !canLoadLv) return;
    await loadXmlForKuLv(kuTrim, lvTrim);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!canGenerate) return;

    setGenerateError(null);
    setGenerateLoading(true);
    try {
      const userInput = {
        floor: floorNum,
        ico: toOptionalString(ico),
        dispozice: toOptionalString(dispozice),
        vymeraM2: toOptionalString(vymeraM2),
        ulice: toOptionalString(ulice),
        co: toOptionalString(co),
        obec: toOptionalString(obec),
        psc: toOptionalString(psc),
      };
      if (overrideDetectedType) {
        userInput.propertyType = overrideType;
      }

      try {
        await saveDocumentDraft(
          {
            kod_ku: kuTrim,
            cislo_lv: lvTrim,
            document_key: DOCUMENT_KEYS.SALE_ORDER_FLAT,
            user_id: selectedSourceUserId || undefined,
          },
          userInput,
          getToken,
        );
      } catch (draftErr) {
        console.warn('saleFlatOrder: failed to save draft', draftErr.message);
      }

      const lvExtra = buildLvNumberPayload({ summary, pdfSummary, initialProperty, lvTrim });
      const payload = { xml, userInput, ...lvExtra };
      const genRes = await apiPost('/api/generate-sale-order-flat', payload, getToken);

      if (genRes.status === 403) {
        const genData = await genRes.json().catch(() => ({}));
        if (genData.code === 'SETUP_REQUIRED' && onSetupRequired) {
          onSetupRequired();
          return;
        }
        throw new Error(genData.error || t('saleFlatOrder.errorGenerate'));
      }

      if (!genRes.ok) {
        const genData = await genRes.json().catch(() => ({}));
        throw new Error(genData.error || t('saleFlatOrder.errorGenerate'));
      }

      const blob = await genRes.blob();
      const disposition = genRes.headers.get('Content-Disposition');
      // Extract filename from Content-Disposition header (RFC 6266-ish).
      const match = disposition && disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1].trim() : `SALE_FLAT_vyplneno-${kuTrim}-${lvTrim}.docx`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setGenerateError(err.message || t('saleFlatOrder.errorUnknown'));
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleGenerateGdpr = async () => {
    if (!hasXml || gdprLoading) return;
    setGdprError(null);
    setGdprLoading(true);
    try {
      const genRes = await apiPost('/api/generate-gdpr', { xml }, getToken);
      if (!genRes.ok) {
        const genData = await genRes.json().catch(() => ({}));
        throw new Error(genData.error || 'Failed to generate GDPR.');
      }
      const blob = await genRes.blob();
      const disposition = genRes.headers.get('Content-Disposition');
      const match = disposition && disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1].trim() : `GDPR_vyplneno-${kuTrim}-${lvTrim}.docx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setGdprError(err.message || 'Failed to generate GDPR.');
    } finally {
      setGdprLoading(false);
    }
  };

  // PDF upload is intentionally disabled in XML-first mode.

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-3 sm:px-4 py-6 sm:py-8 w-full max-w-full min-w-0">
      <div className="w-full max-w-md min-w-0">
        <div className="rounded-2xl border border-slate-200/80 bg-white shadow-lg shadow-slate-200/50 p-4 sm:p-8">
          <h1 className="text-xl font-semibold text-slate-800 tracking-tight mb-1">
            {t('saleFlatOrder.landingTitle')}
          </h1>
          <p className="text-slate-500 text-sm mb-8">{t('saleFlatOrder.landingSubtitle')}</p>

          <form onSubmit={handleLoadLv} className="space-y-5">
            {hasSource ? (
              <div className="space-y-4">
                {summary ? (
                  <LvSummaryAll summary={summary} />
                ) : (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    {t('rentOrder.fromLvIntro')}
                  </div>
                )}

                <div className="space-y-3">
                  <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-2">
                    <label className="text-xs text-slate-700 block">
                      <span className="font-medium">{t('propertyTypeOverride.detectedTypeLabel')}</span>
                      <input
                        type="text"
                        readOnly
                        value={t(`propertyTypeOverride.detectedTypeValue.${detectedPropertyType}`)}
                        className="mt-1 w-full rounded border border-slate-300 bg-slate-100 px-2 py-2 text-sm text-slate-700"
                      />
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={overrideDetectedType}
                        onChange={(e) => setOverrideDetectedType(e.target.checked)}
                      />
                      <span>{t('propertyTypeOverride.overrideToggle')}</span>
                    </label>
                    {overrideDetectedType && (
                      <div>
                        <select
                          value={overrideType}
                          onChange={(e) => setOverrideType(e.target.value)}
                          className="w-full rounded border border-amber-300 bg-white px-2 py-2 text-sm"
                        >
                          <option value={PROPERTY_TYPE_VALUES.LAND}>{t('propertyTypeOverride.detectedTypeValue.land')}</option>
                          <option value={PROPERTY_TYPE_VALUES.HOUSE_OR_OTHER_BUILDING}>{t('propertyTypeOverride.detectedTypeValue.house_or_other_building')}</option>
                          <option value={PROPERTY_TYPE_VALUES.UNIT}>{t('propertyTypeOverride.detectedTypeValue.unit')}</option>
                        </select>
                        <p className="mt-2 text-[11px]">{t('propertyTypeOverride.overrideWarning')}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                      {t('saleFlatOrder.floor')}
                    </label>
                    <p className="text-xs text-slate-400 mb-1">{t('docFormHints.floor')}</p>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={floor}
                      onChange={(e) => setFloor(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      placeholder={t('saleFlatOrder.floorPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                      {t('saleFlatOrder.ico')}
                    </label>
                    <p className="text-xs text-slate-400 mb-1">{t('docFormHints.ico')}</p>
                    <input
                      type="text"
                      value={ico}
                      onChange={(e) => setIco(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      placeholder={t('saleFlatOrder.icoPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                      {t('saleFlatOrder.co')}
                    </label>
                    <input
                      type="text"
                      value={co}
                      onChange={(e) => setCo(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      placeholder={t('saleFlatOrder.coPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                      {t('saleFlatOrder.dispozice')}
                    </label>
                    <p className="text-xs text-slate-400 mb-1">{t('docFormHints.dispozice')}</p>
                    <input
                      type="text"
                      value={dispozice}
                      onChange={(e) => setDispozice(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      placeholder={t('saleFlatOrder.dispozicePlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                      {t('saleFlatOrder.vymeraM2')}
                    </label>
                    <p className="text-xs text-slate-400 mb-1">{t('docFormHints.vymeraM2')}</p>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={vymeraM2}
                      onChange={(e) => setVymeraM2(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      placeholder={t('saleFlatOrder.vymeraM2Placeholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                      {t('saleFlatOrder.ulice')}
                    </label>
                    <input
                      type="text"
                      value={ulice}
                      onChange={(e) => setUlice(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      placeholder={t('saleFlatOrder.ulicePlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                      {t('saleFlatOrder.obec')}
                    </label>
                    <p className="text-xs text-slate-400 mb-1">{t('docFormHints.obec')}</p>
                    <input
                      type="text"
                      value={obec}
                      onChange={(e) => setObec(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      placeholder={t('saleFlatOrder.obecPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                      {t('saleFlatOrder.psc')}
                    </label>
                    <p className="text-xs text-slate-400 mb-1">{t('docFormHints.psc')}</p>
                    <input
                      type="text"
                      value={psc}
                      onChange={(e) => setPsc(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      placeholder={t('saleFlatOrder.pscPlaceholder')}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                  className="w-full py-3 px-4 rounded-xl bg-slate-800 text-white font-medium text-sm hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generateLoading ? t('saleFlatOrder.saving') : t('saleFlatOrder.generateDocAction')}
                </button>
                <button
                  type="button"
                  onClick={handleGenerateGdpr}
                  disabled={gdprLoading || !hasXml}
                  className="w-full py-2.5 px-4 rounded-xl border border-slate-200 text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-50"
                >
                  {gdprLoading ? t('rentOrder.generatingGdpr') : t('saleFlatOrder.generateGdprAction')}
                </button>
                <button
                  type="button"
                  onClick={onBack}
                  className="w-full py-2.5 px-4 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 transition-colors"
                >
                  {t('saleFlatOrder.backMainAction')}
                </button>

                {(generateError || gdprError) && (
                  <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700">
                    {generateError || gdprError}
                  </div>
                )}
              </div>
            ) : (
              <>
                {savedProperties.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                      {t('rentOrder.savedPropertiesLabel')}
                    </label>
                    <select
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      value={selectedPropertyId}
                      onChange={async (e) => {
                        const id = e.target.value;
                        setSelectedPropertyId(id);
                        const prop = savedProperties.find((p) => String(p.id) === id);
                        if (prop) {
                          const kk = String(prop.kod_ku || '').trim();
                          const ll = String(prop.cislo_lv || '').trim();
                          setKu(kk);
                          setLv(ll);
                          if (isPdfBackedProperty(prop)) {
                            setError(null);
                            setLoadLoading(true);
                            try {
                              const { pdfBase64: b64, pdfName: name } = await fetchStoredLvPdf(getToken, {
                                kod_ku: kk,
                                cislo_lv: ll,
                                user_id: isAdmin && prop.user_id ? prop.user_id : undefined,
                              });
                              setPdfBase64(b64);
                              setPdfName(name || 'LV.pdf');
                              setXml(null);
                              setSummary(null);
                            } catch (err) {
                              setError(err.message || t('saleFlatOrder.errorFetch'));
                            } finally {
                              setLoadLoading(false);
                            }
                          } else if (!isAdmin && kk && ll) {
                            loadXmlForKuLv(kk, ll);
                          }
                        }
                      }}
                    >
                      <option value="">{t('rentOrder.savedPropertiesPlaceholder')}</option>
                      {savedProperties.map((p) => (
                        <option key={p.id} value={p.id}>
                          {formatSavedPropertyLabel(p, t)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {!isAdmin && (
                  <p className="text-sm text-slate-600">{t('lvWorkspace.agentNoKuLvHint')}</p>
                )}
                {isAdmin && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                        {t('saleFlatOrder.kuLabel')}
                      </label>
                      <p className="text-xs text-slate-400 mb-1">{t('docFormHints.kuLv')}</p>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={ku}
                        onChange={(e) => setKu(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder={t('saleFlatOrder.kuPlaceholder')}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                        {t('saleFlatOrder.lvLabel')}
                      </label>
                      <p className="text-xs text-slate-400 mb-1">{t('docFormHints.kuLv')}</p>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={lv}
                        onChange={(e) => setLv(e.target.value.replace(/\D/g, '').slice(0, 8))}
                        placeholder={t('saleFlatOrder.lvPlaceholder')}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      />
                    </div>
                  </>
                )}

                {error && (
                  <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700">
                    {error}
                  </div>
                )}

                {isAdmin && (
                  <button
                    type="submit"
                    disabled={!canLoadLv}
                    className="w-full py-3 px-4 rounded-xl bg-slate-800 text-white font-medium text-sm hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadLoading ? t('saleFlatOrder.loading') : t('saleFlatOrder.loadLv')}
                  </button>
                )}
                {/* PDF upload UX temporarily disabled (XML-first mode)
                <div className="pt-2 border-t border-slate-100">
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                    {isAdmin ? t('common.adminPdfUploadLabel') : t('common.pdfUploadLabel')}
                  </label>
                  <p className="text-xs text-slate-400 mb-1">{t('docFormHints.pdfApartment')}</p>
                  <input type="file" accept="application/pdf" onChange={handlePdfUpload} className="block w-full text-sm text-slate-600" />
                  {pdfName && <p className="text-xs text-slate-500 mt-1">{t('common.selectedPdf')}: {pdfName}</p>}
                </div>
                */}
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

